import React from "react";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { HubPrimaryAction } from "../shared/HubPrimaryAction";
import { PAGE_LAYOUT } from "../../layout/pageLayout";
import { DesktopKpiStrip } from "../shared/DesktopKpiStrip";
import {
  DeskEmpty,
  DeskPill,
  DeskRowMenu,
  DeskSearch,
  DeskTable,
  DeskTabs,
  DeskToolbar,
} from "../desktop/DeskTable";
import {
  formatLogDuration,
  formatLogRange,
  groupLogsByDate,
  payrollPill,
  pluralLogs,
  summarizeLogs,
} from "./timeLogModel";
import { TimeLogsActiveFilters } from "./TimeLogsToolbar";

/**
 * Desktop Time Logs workspace: KPI strip, one toolbar, and a timesheet table
 * grouped by day (and by employee for My Team). Same data, actions and
 * navigation as the phone list.
 */
export function TimeLogsDesktop({
  onBack,
  scopeOptions,
  logScope,
  onScopeChange,
  searchTerm,
  onSearch,
  filterChips,
  onOpenFilters,
  onClearFilters,
  logs,
  groupedByEmployee,
  showEmployee,
  onOpen,
  onNew,
  menuItemsFor,
  exportAction = null,
}) {
  const summary = summarizeLogs(logs);
  const unpaid = logs.filter((log) => payrollPill(log).label === "Unpaid").length;
  const paid = logs.length - unpaid;
  const filtersActive = filterChips.length > 0;

  const makeSections = () => {
    if (groupedByEmployee) {
      return groupedByEmployee.flatMap(([employeeName, employeeLogs]) =>
        groupLogsByDate(employeeLogs).map((day) => ({
          key: `${employeeName}-${day.dateIso}`,
          header: (
            <span className="dt-group__row">
              <span>
                {employeeName} · {day.label}
              </span>
              <span className="dt-group__total">
                {day.durationLabel} · {pluralLogs(day.count)}
              </span>
            </span>
          ),
          rows: day.logs,
        })),
      );
    }
    return groupLogsByDate(logs).map((day) => ({
      key: day.dateIso || "unknown",
      header: (
        <span className="dt-group__row">
          <span>{day.label}</span>
          <span className="dt-group__total">
            {day.durationLabel} · {pluralLogs(day.count)}
          </span>
        </span>
      ),
      rows: day.logs,
    }));
  };

  const columns = [
    {
      key: "time",
      label: "Time",
      width: "21%",
      primary: true,
      render: (log) => (
        <span className="dt-strong dt-num">{formatLogRange(log) || "—"}</span>
      ),
    },
    {
      key: "project",
      label: "Project",
      width: showEmployee ? "24%" : "29%",
      render: (log) => (
        <>
          <span className="dt-strong">{log.project_name || "No Project"}</span>
          {log.client_name ? <span className="dt-sub">{log.client_name}</span> : null}
        </>
      ),
    },
    ...(showEmployee
      ? [
          {
            key: "employee",
            label: "Employee",
            width: "16%",
            render: (log) => <span className="dt-strong">{log.employee || "—"}</span>,
          },
        ]
      : []),
    {
      key: "type",
      label: "Work Type",
      width: "14%",
      render: (log) => <span className="dt-muted">{String(log.work_category || "").trim() || "—"}</span>,
    },
    {
      key: "duration",
      label: "Duration",
      align: "right",
      width: "11%",
      render: (log) => <span className="dt-num">{formatLogDuration(log)}</span>,
    },
    {
      key: "status",
      label: "Payroll",
      width: "12%",
      render: (log) => {
        const pill = payrollPill(log);
        return <DeskPill tone={pill.tone === "ok" ? "green" : "gold"}>{pill.label}</DeskPill>;
      },
    },
    {
      key: "actions",
      label: "",
      className: "dt-cell--actions",
      width: "56px",
      render: (log) => (
        <DeskRowMenu
          label="Time log actions"
          items={menuItemsFor ? menuItemsFor(log) : []}
          onSelect={(item) => item.onSelect(log)}
        />
      ),
    },
  ];

  return (
    <CrewPilotModuleShell
      title="Time Logs"
      subtitle="Search, review, and manage employee time"
      onBack={onBack}
      layout={PAGE_LAYOUT.COMMAND}
      primary
      compact
      headerActions={
        <div className="cdh-actions">
          <DeskSearch
            value={searchTerm}
            onChange={onSearch}
            placeholder="Search time logs…"
            ariaLabel="Search time logs"
          />
          {exportAction}
          {onNew ? (
            <HubPrimaryAction onClick={onNew} label="New Time Log" ariaLabel="New Time Log" />
          ) : null}
        </div>
      }
    >
      <DesktopKpiStrip
        ariaLabel="Time log summary"
        tiles={[
          { key: "time", value: summary.durationLabel, label: "Total Time", tone: "gold" },
          { key: "count", value: String(summary.count), label: summary.count === 1 ? "Time Log" : "Time Logs", tone: "blue" },
          { key: "unpaid", value: String(unpaid), label: "Unpaid", tone: "red" },
          { key: "paid", value: String(paid), label: "Paid", tone: "green" },
        ]}
      />

      <DeskToolbar>
        <DeskTabs options={scopeOptions} value={logScope} onChange={onScopeChange} ariaLabel="Time log scope" />
        <span className="dt-toolbar__grow" />
        <span className="dt-summary" data-testid="time-log-summary" aria-live="polite">
          {pluralLogs(summary.count)} · {summary.durationLabel}
        </span>
        <button
          type="button"
          className={`dt-btn${filtersActive ? " has-filter" : ""}`}
          onClick={onOpenFilters}
          aria-label={filtersActive ? `Filters, ${filterChips.length} active` : "Filters"}
        >
          Filters{filtersActive ? ` (${filterChips.length})` : ""}
        </button>
      </DeskToolbar>

      <TimeLogsActiveFilters chips={filterChips} onClearAll={onClearFilters} />

      {logs.length === 0 ? (
        <DeskEmpty title={searchTerm || filtersActive ? "No time logs match" : "No time logs found"}>
          {searchTerm || filtersActive
            ? "No time logs match your search and filters."
            : "Time logs appear here once work is recorded."}
        </DeskEmpty>
      ) : (
        <DeskTable
          testId="time-log-table"
          caption="Time logs"
          columns={columns}
          groups={makeSections()}
          rowKey={(log) => log.id}
          onOpen={onOpen}
          openLabel={(log) => `Open time log: ${formatLogDuration(log)}, ${log.project_name || "No Project"}`}
        />
      )}
    </CrewPilotModuleShell>
  );
}
